import { ROTATE_OVERLAY_WORDS } from './rotate-overlay-words.js';

/**
 * THE ROTATE SCREEN IN THE VISITOR'S LANGUAGE, FROM ITS FIRST PAINT.
 *
 * Moonfall is landscape-only on a phone, so a phone held upright opens on
 * "Rotate your device" (index.html `#rotate-overlay`, drawn by responsive.css
 * from the first paint). index.html carries the screen's English, and any
 * other language used to be written in by i18n.js once the 1.9 MB main bundle
 * and the language file had both arrived: a player of another language read
 * the English first and then watched the screen change into their own words
 * (13 October 2026, measured in Chrome).
 *
 * WHY HERE, IN THE HEAD BUNDLE. This file rides in dist/app.pre1.js, the one
 * script in <head>, so it has run before the body is parsed. It watches the
 * parser and writes the visitor's lines into the card in the same turn
 * the parser finishes them, so no paint can come between (the way
 * in-app-browser-flag.js builds its sentence):
 *
 *   data-rotate-copy="pending"   on <html> until every line is written. A
 *                                slow link delivers the page in pieces, and the
 *                                browser paints between them, so a card the
 *                                parser is still inside would show a line of
 *                                English; responsive.css keeps it unseen.
 *   lang="<language>"            on each line, until i18n.js has given the
 *                                page itself that language. A Japanese, Korean
 *                                or Chinese line is drawn in its own language's
 *                                faces from the first paint, not drawn again
 *                                when <html lang> changes.
 *
 * The language is the one the head's language script chose (index.html
 * `moonfall-i18n-preload`: the link's `?lang=`, then the saved choice, then
 * English), which is the one i18n.js goes on to show
 * (tests/unit/i18n/translationPreload.test.js holds the two together). So
 * i18n.js finds each line already saying what it would write, and writes
 * nothing.
 *
 * AN ENGLISH PAGE GETS NOTHING, and neither does an app's own browser: there
 * the card is a different one, held for its language by in-app-browser-flag.js
 * (which runs just before this, in the same bundle).
 *
 * THE LINES. The title, the paragraph, and the three rotation-lock lines under
 * it (17 October 2026), of which responsive.css shows the one for this phone
 * (phone-os-flag.js). The card is whole once the parser is past the last of
 * them.
 *
 * The words are rotate-overlay-words.js, a copy of ui.json's.
 * Contract: tests/unit/client/rotateOverlayCopy.test.js.
 */
(function () {
  'use strict';

  /* [element, its line in rotate-overlay-words.js], in the order of the markup. */
  const LINES = [
    ['rotate-overlay-title', 'title'],
    ['rotate-overlay-body', 'body'],
    ['rotate-overlay-lock-ios', 'lockIos'],
    ['rotate-overlay-lock-android', 'lockAndroid'],
    ['rotate-overlay-lock-other', 'lockOther'],
  ];
  const MARK_PENDING = 'data-rotate-copy';
  /* i18n.js raises this on `window` once, at the end of `init`, after it has
     set <html lang> and written the visitor's language into the page. */
  const LANGUAGE_READY_EVENT = 'moonfall:i18nready';

  const root = document.documentElement;
  if (root.hasAttribute('data-in-app-browser')) return;

  /**
   * The language this page is about to show, as the head's language script
   * resolved it: its request for the language file names it. Null when it
   * cannot be read (that script failed), and the page stays as it was.
   */
  function bootLanguage() {
    try {
      const request = /** @type {any} */ (window).__moonfallTranslationRequest;
      const match = request && typeof request.url === 'string'
        ? /\/ui\.([A-Za-z-]+)\.json(?:[?#]|$)/.exec(request.url)
        : null;
      return match ? match[1] : null;
    } catch (_) {
      return null;
    }
  }

  const language = bootLanguage();
  if (!language || !Object.prototype.hasOwnProperty.call(ROTATE_OVERLAY_WORDS, language)) return;
  const words = ROTATE_OVERLAY_WORDS[language];

  /** @type {MutationObserver | null} */
  let observer = null;
  /** @type {HTMLElement[]} */
  const lent = [];
  let written = false;

  root.setAttribute(MARK_PENDING, 'pending');

  /**
   * The parser is past this element: something follows it, there or further
   * up, or the document is done. Until then its text may be half written.
   * @param {Node} element
   */
  function parsedPast(element) {
    if (document.readyState !== 'loading') return true;
    /** @type {Node | null} */
    let node = element;
    for (; node && node !== root; node = node.parentNode) {
      if (node.nextSibling) return true;
    }
    return false;
  }

  /**
   * @param {HTMLElement} element
   * @param {string} text
   */
  function lend(element, text) {
    element.setAttribute('lang', language);
    if (element.textContent !== text) element.textContent = text;
    lent.push(element);
  }

  /** Writes every line once the parser is past the last, then lets the card be seen. */
  function write() {
    if (written) return true;
    const elements = LINES.map(([id]) => document.getElementById(id));
    const last = elements[elements.length - 1];
    if (elements.some((element) => !element) || !parsedPast(last)) return false;
    elements.forEach((element, index) => lend(/** @type {HTMLElement} */ (element), words[LINES[index][1]]));
    written = true;
    root.removeAttribute(MARK_PENDING);
    return true;
  }

  function stopWatching() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    document.removeEventListener('DOMContentLoaded', lastLook);
  }

  /* The document is parsed. Whatever is there is written, and the card is let
     be seen whatever happened: a mark left on would hide it for good. */
  function lastLook() {
    write();
    root.removeAttribute(MARK_PENDING);
    stopWatching();
  }

  /* i18n.js owns these labels from here on, as it owns every other: the page's
     own language is the lines' now, so the loan goes back, and a language the
     player picks later is not drawn under this one. */
  function giveBack() {
    lent.splice(0).forEach((element) => element.removeAttribute('lang'));
  }
  window.addEventListener(LANGUAGE_READY_EVENT, giveBack, { once: true });

  if (!write()) {
    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(() => {
        if (write()) stopWatching();
      });
      observer.observe(root, { childList: true, subtree: true });
    }
    document.addEventListener('DOMContentLoaded', lastLook);
  }
})();
